// useWorkspaceLifecycle.ts — Suspend and Resume for the open session's
// workspace.
//
// Both act on the workspace, not the session: every session in it shares
// the one Pod, so every one of them goes down (or comes back) together.
// The phase is flipped optimistically; the manager socket then corrects
// each row.
import React, { useCallback, useState } from 'react';
import { workspacesApi } from '../api';
import type { SessionPhase } from './live/sessionLive';
import type { BootLogLine } from './sessionRows';

export interface WorkspaceLifecycleArgs {
  selectedWorkspaceId: string | null;
  refreshSessions: () => Promise<void>;
  setSessionPhase: React.Dispatch<React.SetStateAction<SessionPhase | null>>;
  setBootLogs: React.Dispatch<React.SetStateAction<BootLogLine[]>>;
  setStreamError: (msg: string) => void;
}

export interface WorkspaceLifecycle {
  /** A suspend or resume is in flight. */
  lifecycleBusy: boolean;
  handleSuspend: () => Promise<void>;
  handleResume: () => Promise<void>;
}

export function useWorkspaceLifecycle({
  selectedWorkspaceId, refreshSessions, setSessionPhase, setBootLogs, setStreamError,
}: WorkspaceLifecycleArgs): WorkspaceLifecycle {
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  // Suspend: tear down the Pod, keep the volumes, rows and history.
  const handleSuspend = useCallback(async () => {
    if (!selectedWorkspaceId || lifecycleBusy) return;
    console.debug('[sessions] suspending workspace', selectedWorkspaceId);
    setLifecycleBusy(true);
    try {
      await workspacesApi.suspend(selectedWorkspaceId);
      setSessionPhase('suspended');
      await refreshSessions();
    } catch (err) {
      setStreamError(`suspend failed: ${err}`);
    } finally {
      setLifecycleBusy(false);
    }
  }, [selectedWorkspaceId, lifecycleBusy, refreshSessions, setSessionPhase, setStreamError]);

  // Resume re-provisions the workspace's Pod and flips the UI back to
  // provisioning at once. The /events stream picks up the boot logs and
  // the eventual ready transition.
  const handleResume = useCallback(async () => {
    if (!selectedWorkspaceId || lifecycleBusy) return;
    console.debug('[sessions] resuming workspace', selectedWorkspaceId);
    setLifecycleBusy(true);
    try {
      await workspacesApi.resume(selectedWorkspaceId);
      setSessionPhase('provisioning');
      setBootLogs([]);
      await refreshSessions();
    } catch (err) {
      setStreamError(`resume failed: ${err}`);
    } finally {
      setLifecycleBusy(false);
    }
  }, [selectedWorkspaceId, lifecycleBusy, refreshSessions, setSessionPhase, setBootLogs, setStreamError]);

  return { lifecycleBusy, handleSuspend, handleResume };
}
